type ResourceCacheEntry<T> = {
  promise: Promise<T> | null
  value: T | undefined
  version: number
}

const resourceCache = new Map<string, ResourceCacheEntry<unknown>>()
let resourceCacheScope = "default"

export function setResourceCacheScope(scope: string) {
  const nextScope = scope.trim() || "default"
  if (nextScope === resourceCacheScope) {
    return
  }

  resourceCacheScope = nextScope
  resourceCache.clear()
}

export function readCachedResource<T>(key: string): T | undefined {
  return resourceCache.get(getScopedResourceCacheKey(key))?.value as T | undefined
}

export function writeCachedResource<T>(key: string, value: T): T {
  const entry = getResourceCacheEntry<T>(key)
  entry.value = value
  entry.version += 1
  return value
}

export function fetchCachedResource<T>(
  key: string,
  load: () => Promise<T>,
  options: { force?: boolean } = {},
): Promise<T> {
  const entry = getResourceCacheEntry<T>(key)
  if (!options.force && entry.value !== undefined) {
    return Promise.resolve(entry.value)
  }
  if (!options.force && entry.promise) {
    return entry.promise
  }

  const loadVersion = options.force ? entry.version + 1 : entry.version
  if (options.force) {
    entry.version = loadVersion
  }
  const loadPromise = load()
    .then((value) => {
      if (entry.version === loadVersion) {
        entry.value = value
        entry.version += 1
        return value
      }
      return entry.value !== undefined ? entry.value : value
    })
    .finally(() => {
      if (entry.promise === loadPromise) {
        entry.promise = null
      }
    })
  entry.promise = loadPromise

  return entry.promise
}

function getResourceCacheEntry<T>(key: string): ResourceCacheEntry<T> {
  const scopedKey = getScopedResourceCacheKey(key)
  let entry = resourceCache.get(scopedKey) as ResourceCacheEntry<T> | undefined
  if (!entry) {
    entry = { promise: null, value: undefined, version: 0 }
    resourceCache.set(scopedKey, entry as ResourceCacheEntry<unknown>)
  }
  return entry
}

function getScopedResourceCacheKey(key: string) {
  return `${resourceCacheScope.length}:${resourceCacheScope}:${key}`
}
